import React from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

/**
 * Decrypt Script Modal Component
 * Modal for generating deanonymization scripts
 */
export const DecryptScriptModal = ({
  isOpen,
  isGenerating,
  selectedLog,
  decryptDownloadUrl,
  onGenerateDecryption,
  onClose,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[999999]"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[999999] p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Generate Decryption Script
                </h3>
                <p className="text-sm text-gray-500">
                  Deanonymize previously protected data
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {selectedLog && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Log ID</span>
                  <code className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                    {selectedLog.log_id}
                  </code>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Database</span>
                  <span className="font-medium text-gray-800">
                    {selectedLog.details?.database}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tables</span>
                  <span className="font-medium text-gray-800">
                    {selectedLog.details?.tables?.map((t) => t.table).join(", ")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Method</span>
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                    {selectedLog.details?.method}
                  </span>
                </div>
              </div>
            )}

            {decryptDownloadUrl && (
              <div className="border-2 border-green-200 bg-green-50 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm font-semibold text-green-800">
                    Decryption script ready!
                  </p>
                </div>
                <a
                  href={decryptDownloadUrl}
                  download={`decrypt_script_${selectedLog?.log_id?.slice(0, 8)}.sql`}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </a>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-all font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onGenerateDecryption}
              disabled={isGenerating}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  Generate Decryption Script
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

DecryptScriptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  selectedLog: PropTypes.shape({
    log_id: PropTypes.string,
    details: PropTypes.shape({
      database: PropTypes.string,
      tables: PropTypes.arrayOf(PropTypes.object),
      method: PropTypes.string,
    }),
  }),
  decryptDownloadUrl: PropTypes.string,
  onGenerateDecryption: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
